import { JSX } from 'preact';
import { route } from 'preact-router';
import { Signal } from '@preact/signals';
import { ApiError, ArchiveImg, BrowserInfo, ClassifierCountries, CountryID, FileDownloadDetails, KYCInfoInsertIndividualPerson, KYCPersonChathost, ProfileImg, ValidationError } from '@ccmn/rhino';
import { UAParser } from 'ua-parser-js';
import { ROUTE_GUARDED, ROUTE_PROMO, SITE_ID, RESPONSE, DEFAULT_CURRENCY_CODE, TIME_CONSTANTS } from './constants';
import { STATUS } from 'src/components/button';

type URLParts = 'hash' | 'host' | 'hostname' | 'href' | 'origin' | 'pathname' | 'port' | 'protocol' | 'search';
interface DurationFormat {
    hours: number;
    minutes: number;
    seconds: number;
}

export const scrollToDiv = (selector: string) => {
    const element = document.querySelector<HTMLElement>(selector);
    element?.scrollIntoView();
};

export const cleanURL = (url: string, part?: URLParts) => {
    const baseURL = window.location.origin;
    const urlParts = new URL(url, baseURL);

    if (part) {
        return urlParts[part];
    }

    return `${urlParts.pathname}${urlParts.search}${urlParts.hash}`;
};

export const redirectURL = (url: string, replaceHistory: boolean = true): string | boolean => {
    if (isRouteGuarded(url)) {
        return route(url, replaceHistory);
    }

    return (location.href = url);
};

export const getBrowserParams = (): BrowserInfo => {
    const parser = new UAParser();
    const result = parser.getResult();
    let deviceData;

    if (result.device.vendor && result.device.model) {
        deviceData = { bi_device: `${result.device.vendor} ${result.device.model}` };
    }

    return {
        ...deviceData,
        bi_browser: result.browser.name,
        bi_browserver: result.browser.version,
        bi_os: result.os.name,
        bi_osver: result.os.version,
        bi_viewport: `${window.innerWidth}x${window.innerHeight}`,
        bi_deviceres: `${window.screen.width}x${window.screen.height}`,
        bi_availres: `${window.screen.availWidth}x${window.screen.availHeight}`,
        bi_pixratio: window.devicePixelRatio,
    };
};

export const isRouteGuarded = (url: string) => {
    const path = cleanURL(url, 'pathname');

    return ROUTE_GUARDED.includes(path);
};

export const isPromoRoute = (url: string) => {
    const path = cleanURL(url, 'pathname');

    return ROUTE_PROMO.includes(path);
};

export const isStudioRoute = (url: string) => {
    const path = cleanURL(url, 'pathname');

    return path.startsWith('/studio');
};

export const formatDate = (datetime?: string | undefined) => {
    const date = datetime ? new Date(datetime) : new Date();

    return date.toLocaleString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
};

export const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);

    return date.toLocaleString('en-US', { timeZone: 'UTC', hour: 'numeric', minute: 'numeric', hour12: true });
};

export const secondsToDuration = (durationInSeconds: number = 0): DurationFormat => {
    const hours = Math.floor(durationInSeconds / TIME_CONSTANTS.SECONDS_PER_HOUR);
    const minutes = Math.floor((durationInSeconds - hours * TIME_CONSTANTS.SECONDS_PER_HOUR) / TIME_CONSTANTS.SECONDS_PER_MINUTE);
    const seconds = durationInSeconds - hours * TIME_CONSTANTS.SECONDS_PER_HOUR - minutes * TIME_CONSTANTS.SECONDS_PER_MINUTE;

    return { hours, minutes, seconds };
};

export const leadingZeros = (value: number, length: number): string => {
    return `${value}`.padStart(length, '0');
};

export const formatDuration = (duration: number): string => {
    const secondsToFullMinute = duration % TIME_CONSTANTS.SECONDS_PER_MINUTE;
    const durationRoundToMinutes = duration + (secondsToFullMinute ? TIME_CONSTANTS.SECONDS_PER_MINUTE - secondsToFullMinute : 0);
    const durationObj = secondsToDuration(durationRoundToMinutes);
    const unitMap: Record<keyof Omit<DurationFormat, 'seconds'>, string> = {
        hours: 'h',
        minutes: 'm',
    };

    const zerosMap: Record<keyof Omit<DurationFormat, 'seconds'>, number> = {
        hours: 0,
        minutes: 2,
    };

    const format = Object.keys(durationObj).map((key: keyof Omit<DurationFormat, 'seconds'>) => {
        return durationObj[key] ? `${leadingZeros(durationObj[key], zerosMap[key])}${unitMap[key]}` : '';
    });

    return format
        .filter((entry: string): string => {
            return entry;
        })
        .join(' ')
        .trim();
};

export const formatVideoDuration = (duration: number): string => {
    const durationObj = secondsToDuration(duration);
    const zerosMap: Record<keyof DurationFormat, number> = {
        hours: 2,
        minutes: 2,
        seconds: 2,
    };

    const format = Object.keys(durationObj)
        .filter((key: keyof DurationFormat) => {
            return !(key === 'hours' && !durationObj[key]);
        }) // filtering out empty hours for video duration
        .map((key: keyof DurationFormat) => {
            return `${leadingZeros(durationObj[key] || 0, zerosMap[key])}`;
        });

    return format.join(':');
};

export const getTimestamp = (date: string): number => {
    return new Date(date).getTime() / 1000;
};

export const generateProfileURL = (username: string, isPromo: boolean = false) => {
    if (isPromo) {
        return `/cammodel-${username}?cf=8`;
    }

    return `/cammodel-${username}`;
};

export const generateProfilePictureURL = (path: string, size: ProfileImg | ArchiveImg, picturePath: string | undefined) => {
    if (picturePath === undefined) {
        throw new Error('picturePath should be defined');
    }
    return `${path}${size}/${SITE_ID}${picturePath}`;
};

export const getOrdinal = (day: number) => {
    const j = day % 10;
    const k = day % 100;

    if (j === 1 && k !== 11) {
        return 'st';
    }

    if (j === 2 && k !== 12) {
        return 'nd';
    }

    if (j === 3 && k !== 13) {
        return 'rd';
    }

    return 'th';
};

export const handleResponseErrors = (response: ApiError, status: Signal, error: Signal) => {
    status.value = STATUS.TYPING;
    if (response.status === 400 || response.status === 403) {
        error.value = response.body.detail;
    } else if (response.status === 401) {
        error.value = RESPONSE.UNAUTHORIZED;
    } else if (response.status === 422) {
        const body: Array<ValidationError> = response.body.detail;

        error.value = body.map((err: ValidationError) => {
            return err.msg;
        });
    } else {
        error.value = RESPONSE.SERVER_ERROR;
    }
};

export const downloadFile = (file: FileDownloadDetails) => {
    const link = document.createElement('a');
    link.href = `data:${file.content_type};base64,${file.content}`;
    link.download = file.file_name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const getMimeTypeFromArrayBuffer = (arrayBuffer: ArrayBuffer) => {
    const uint8arr = new Uint8Array(arrayBuffer);
    const length = 4;

    if (uint8arr.length >= length) {
        const signatureArr = new Array(length);

        for (let i = 0; i < length; i++) {
            signatureArr[i] = new Uint8Array(arrayBuffer)[i].toString(16);
        }

        const signature = signatureArr.join('').toUpperCase();

        switch (signature) {
            case '89504E47':
                return 'image/png';
            case '47494638':
                return 'image/gif';
            case 'FFD8FFDB':
            case 'FFD8FFEE':
            case 'FFD8FFE0':
            case 'FFD8FFE1':
            case 'FFD8FFE2':
                return 'image/jpeg';
            default:
                return null;
        }
    }

    return null;
};

const substractYearsFromDate = (date: Date, years: number) => {
    return new Date(date.setUTCFullYear(date.getUTCFullYear() - years));
};

export const isYearInRange = (date: string): null | string => {
    const individualDate = new Date(date);
    const rangeEnd = substractYearsFromDate(new Date(), 18);
    const rangeStart = substractYearsFromDate(new Date(), 100);

    if (individualDate >= rangeStart && individualDate <= rangeEnd) {
        return null;
    }

    return `Year should be between ${rangeStart.getUTCFullYear()} and ${rangeEnd.getUTCFullYear()}`;
};

export const getCountryById = (countryID: CountryID | undefined, countryList: ClassifierCountries[] | undefined) => {
    if (countryList) {
        const country = countryList.find((obj: ClassifierCountries) => {
            return obj.id === countryID;
        });
        return country?.Name;
    }
    return '';
};

export const isCountryInEU = (countryID: CountryID | undefined, countryList: ClassifierCountries[] | undefined): boolean => {
    if (countryList) {
        const country = countryList.find((obj: ClassifierCountries) => {
            return obj.id === countryID;
        });

        if (!country) {
            return false;
        }

        return country?.isEU;
    }
    return false;
};

export const setRadioStateForAccount = (person: KYCPersonChathost | KYCInfoInsertIndividualPerson, isTINHome: Signal, isTINAbroad: Signal, isTIN3Visible: Signal) => {
    if (person.TINplaceOfBirth) {
        isTINHome.value = false;
    }

    if (person.TIN) {
        isTINHome.value = true;
    }

    if (person.TIN2) {
        isTINAbroad.value = true;
    }

    if (person.TIN3) {
        isTIN3Visible.value = true;
    }
};

export const formatAmount = (amount: number, fractionDigits: number = 2, currency: string = DEFAULT_CURRENCY_CODE): string => {
    return Intl.NumberFormat('en-US', {
        style: 'currency',
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits,
        currency,
    }).format(amount);
};

export const combineClasses = (...classNames: (string | JSX.Signalish<string | undefined> | undefined | null | false)[]): string => {
    return classNames.filter(Boolean).join(' ');
};
